<template>
  <div
    ref="mapContainer"
    class="map-container mx-3 my-3"
  ></div>
</template>

<script>
import {
  ref, onMounted, onUnmounted, watch,
} from '@vue/composition-api'
import { Loader } from '@googlemaps/js-api-loader'

export default {
  name: 'GoogleMap',

  props: {
    points: {
      type: Array,
      required: true,
    },
    selectedPoint: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const mapContainer = ref(null)
    const googleMap = ref(null)
    const markers = ref([])
    const isMapInitialized = ref(false)
    const AdvancedMarkerElement = ref(null)

    /**
     * Crea l'elemento del marker personalizzato
     */
    const createMarkerElement = point => {
      const isSelected = props.selectedPoint?.pudoId === point.pudoId

      const markerElement = document.createElement('div')
      markerElement.className = 'custom-marker'
      markerElement.innerHTML = `
<div
  class="marker-container ${isSelected ? 'selected' : ''}"
  style="
    height: ${isSelected ? '40px' : '14px'};
    width: ${isSelected ? '40px' : '14px'};
    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADgAAAA4CAMAAACfWMssAAAAY1BMVEUAAAD///9qampFRUWhoaGRkZGVlZV+fn5MTExubm6cnJwGBgYYGBimpqYQEBAjIyMqKiqJiYnc3Ny+vr43NzdSUlJ1dXVXV1c8PDwwMDD5+fmzs7Pw8PDQ0NBhYWHGxsbn5+fKJnSMAAABlklEQVRIie1T27KkIAxMQAHxhqB4QdH//8qDl6nZqsGdrX3a2rIfpI3pShsSgAcPHvyv6Lb+M5jr9KswxUjOgPp7SRYL8vGrTmLyGaR/UHE0bSTqyK2gW+t8P8kUclwDl7cauAvN2cobmdYOU1wD45hMDDeNxR4X6DRm2ByfPpEPeoYsn8usgiohKEUtFxKUnGyN8kHY0KhwQXGcHB0kKA/uQxGF05kw4BYVZsvh62hMnlQHL4wPl/rqCRMxHZ2ai7k5VL1egvsFr7hCExP2l7vdc+Xnq8qqYX0ZrGYXtdq70x60FloznLwJg+D4m8ewoj/JhiN7lUd8j+iEcWGdGQ0dGNMn71ntguXOom3rdPKRjXmVYphOU7pVKbJfqthw93OJ3Z0ugGpRgMFW6CurvBppEqh+ozvB7deUOEJDQORDocbKU0JBtIOCQOq8uFE0W11BuVgCXkjCreWE8p6NUlLbljQld1YMWjTHRgjCLN9BM8U6ycD2iW94dOR2qGKWYfkC4T6sSHFwUOdDAf/Lf3/w4ME/ih8byROR9Be+NgAAAABJRU5ErkJggg==');
    background-size: cover;
    border-radius: 50%;
    ${isSelected ? 'box-shadow: 0 0 0 2px gold;' : ''}
    transition: all 0.2s ease;
  "
>
  <div class="marker-point"></div>
  <div class="marker-pulse"></div>
</div>
      `

      return markerElement
    }

    /**
     * Aggiorna i marker sulla mappa
     */
    const updateMarkers = () => {
      if (!isMapInitialized.value || !googleMap.value || !AdvancedMarkerElement.value) {
        console.log('Not ready to update markers:', {
          isInitialized: isMapInitialized.value,
          hasMap: !!googleMap.value,
          hasAdvancedMarker: !!AdvancedMarkerElement.value,
        })

        return
      }

      // Clear existing markers
      markers.value.forEach(marker => marker.map = null)
      markers.value = []

      // Add new markers
      props.points.forEach(point => {
        const position = {
          lat: parseFloat(point.latitude),
          lng: parseFloat(point.longitude),
        }

        const markerElement = createMarkerElement(point)

        const marker = new AdvancedMarkerElement.value({
          map: googleMap.value,
          position,
          title: point.pointName,
          content: markerElement,
        })

        marker.addListener('gmp-click', () => emit('point-selected', point))
        markers.value.push(marker)
      })

      // Center map on first point if no point is selected
      if (props.points.length > 0 && !props.selectedPoint) {
        console.log('Centering map on first point')
        googleMap.value.setCenter({
          lat: parseFloat(props.points[0].latitude),
          lng: parseFloat(props.points[0].longitude),
        })
      }
    }

    /**
     * Inizializza Google Maps
     */
    const initializeMap = () => {
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
        version: 'beta', // Specifichiamo la versione beta
        libraries: ['marker', 'places'], // Pre-carichiamo la libreria marker
        channel: 'beta', // Specifichiamo il canale beta
      })

      loader.load().then(google => google.maps.importLibrary('marker')).then(markerLibrary => {
        AdvancedMarkerElement.value = markerLibrary.AdvancedMarkerElement

        // Default center (Italy)
        const defaultCenter = { lat: 43.769715, lng: 11.255279 }

        googleMap.value = new google.maps.Map(mapContainer.value, {
          center: defaultCenter,
          zoom: 13,
          mapId: 'PudoMapList', // Necessario per la versione beta
          // Disabilita i controlli non desiderati
          fullscreenControl: false,
          mapTypeControl: false, // Rimuove l'opzione satellite/mappa
          streetViewControl: false, // Opzionale: rimuove anche Street View
        })

        isMapInitialized.value = true

        // If we already have points when map is initialized, update markers
        if (props.points.length > 0) {
          updateMarkers()
        }
      }).catch(err => {
        console.error('Error loading Google Maps:', err)
      })
    }

    // Watch for changes in points
    watch(
      () => props.points,
      newPoints => {
        console.log('Points changed:', newPoints.length)
        if (newPoints.length > 0) {
          updateMarkers()
        }
      },
      { deep: true },
    )

    // Watch for selected point changes
    watch(
      () => props.selectedPoint,
      newPoint => {
        if (newPoint && googleMap.value) {
          googleMap.value.setCenter({
            lat: parseFloat(newPoint.latitude),
            lng: parseFloat(newPoint.longitude),
          })
          googleMap.value.setZoom(15)
        } else {
          googleMap.value.setZoom(13)
        }

        updateMarkers()
      },
    )

    // Lifecycle hooks
    onMounted(() => {
      console.log('Component mounted, initializing map')
      initializeMap()
    })

    onUnmounted(() => {
      markers.value.forEach(marker => marker.map = null)
      markers.value = []
    })

    return {
      mapContainer,
    }
  },
}
</script>

<style scoped>
.map-container {
  min-height: 400px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ddd;
}

/* Custom Marker Styles */
.custom-marker {
  cursor: pointer;
}

.marker-container {
  position: relative;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
}

.marker-point {
  position: absolute;
  width: 14px;
  height: 14px;
  background-color: #757575;
  border: 2px solid #ffffff;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  transition: all 0.3s ease;
}

.marker-pulse {
  position: absolute;
  width: 24px;
  height: 24px;
  background-color: rgba(117, 117, 117, 0.2);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 2s infinite;
}

.marker-container.selected .marker-point {
  background-color: #1976D2;
  width: 18px;
  height: 18px;
}

.marker-container.selected .marker-pulse {
  background-color: rgba(25, 118, 210, 0.2);
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}
</style>
